// React
import type React from 'react';

// Mantine
import {
  Box,
  Group,
  type MantineColor,
  Stack,
  Title,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';

// Styles
import { getIconBadgeStyles } from './MetricCardContainerStyles';

// Custom Components
import { StyledPaper } from './StyledPaper';

interface MetricCardContainerProps {
  title: string;
  icon?: React.ReactNode;
  isLoading?: boolean;
  children: React.ReactNode;
  isMobile?: boolean;
  testId?: string;
  accent?: MantineColor;
  onClick?: () => void;
  subtitle?: React.ReactNode;
  topRightBadge?: React.ReactNode;
}

export const MetricCardContainer: React.FC<MetricCardContainerProps> = ({
  title,
  icon,
  isLoading,
  children,
  isMobile,
  testId = 'metric-card',
  accent = 'blue',
  onClick,
  subtitle,
  topRightBadge,
}) => {
  const theme = useMantineTheme();
  const scheme = useComputedColorScheme('dark');
  const iconStyles = getIconBadgeStyles(theme, accent, scheme);

  return (
    <StyledPaper isLoading={isLoading} isMobile={isMobile} testId={testId}>
      <Group
        align="flex-start"
        mb="lg"
        gap="sm"
        wrap="nowrap"
        justify="space-between"
        onClick={onClick}
      >
        {isMobile && subtitle ? (
          <Group gap={'md'} align="center" style={{ flexGrow: 1 }}>
            <Title order={3} fw={600} style={{ lineHeight: 1 }}>
              {title}
            </Title>
            <Box data-testid="metric-card-subtitle" style={{ lineHeight: 1 }}>
              {subtitle}
            </Box>
          </Group>
        ) : (
          <Stack
            gap={subtitle ? 4 : 0}
            align="flex-start"
            justify="flex-start"
            style={{ flexGrow: 1 }}
          >
            <Title order={3} fw={600}>
              {title}
            </Title>
            {subtitle && (
              <Box data-testid="metric-card-subtitle" style={{ lineHeight: 1 }}>
                {subtitle}
              </Box>
            )}
          </Stack>
        )}
        {topRightBadge && (
          <Box
            data-testid="metric-card-top-right-badge"
            style={{ flexShrink: 0 }}
          >
            {topRightBadge}
          </Box>
        )}
        {!topRightBadge && !isMobile && icon && (
          <Box data-testid="metric-card-icon" style={iconStyles}>
            {icon}
          </Box>
        )}
      </Group>
      <Stack gap={isMobile ? 'md' : 'sm'} style={{ flexGrow: 1 }}>
        {children}
      </Stack>
    </StyledPaper>
  );
};
